import React from 'react';
import classNames from 'classnames';
import {useCheckbox} from '@react-aria/checkbox';
import {useToggleState} from '@react-stately/toggle';
import {VisuallyHidden} from '@react-aria/visually-hidden';

import {useFocusRing} from '@react-aria/focus';

import {CheckIcon} from '../../../icons/check';

import {TypographySize, useTypography} from '../../../hooks/typography';

import type {ViewProps, Props} from './types';
import {CheckboxSize} from './constants';

import styles from './styles.module.css';

export {CheckboxSize} from './constants';

export const CheckboxView = React.forwardRef<HTMLInputElement, ViewProps>((props, ref) => {
    const {
        checked,
        checkboxSize = CheckboxSize.MEDIUM,
        disabled = false,
        destructive = false,
        label,
        focused,
        invalid,
        inline,
        spaceful,
        ...inputProps
    } = props;
    const typographyCls = useTypography({
        size: TypographySize.BodyL,
    });

    return (
        <label
            className={classNames(
                styles.label,
                styles.container,
                typographyCls,
                !disabled && styles.withHover,
                focused && styles.focused,
                invalid && styles.invalid,
                inline && styles.inline,
                spaceful && styles.spaceful,
            )}
        >
            <VisuallyHidden>
                <input {...inputProps} ref={ref} />
            </VisuallyHidden>
            <span
                className={classNames(
                    styles.checkMarkContainer,
                    styles[checkboxSize],
                    destructive && styles.destructive,
                    disabled && styles.disabled,
                    checked && styles.checked,
                    styles.checkbox,
                )}
            >
                <CheckIcon className={classNames(styles.icon, checked && styles.checked)} />
            </span>
            <span className={styles.labelText}>{label}</span>
        </label>
    );
});

export const Checkbox: React.VoidFunctionComponent<Props> = (props) => {
    const {label: checkboxLabel, invalid, destructive, checkboxSize = CheckboxSize.MEDIUM} = props;
    const state = useToggleState(props);
    const ref = React.useRef<HTMLInputElement>(null);

    const {inputProps} = useCheckbox(props, state, ref);
    const {isFocusVisible, focusProps} = useFocusRing();

    return (
        <CheckboxView
            ref={ref}
            label={checkboxLabel}
            checked={state.isSelected}
            disabled={inputProps.disabled}
            invalid={invalid}
            destructive={destructive}
            focused={isFocusVisible}
            checkboxSize={checkboxSize}
            {...inputProps}
            {...focusProps}
        />
    );
};
